import React from 'react';
import PropTypes from 'prop-types';
import { Price } from '../price';
import { PriceIsMps } from '../price_is_mps';
import { generateClassName } from '../../../../../_modules/helper/generate_class_name';
import { RowTwoCells } from '../row_two_cells';
import { getTranslationElement } from '../../../../helper/getTranslationElement';
import onCrossSellDetailsClick from '../../actions/on_cross_sell_details_click';
import { ShippingPrice } from '../shipping_price';

const lazyClassNames = generateClassName({
  'product-price': true,
  'product-price--no-details': ({ showDetails }) => showDetails,
  'underline cursor-pointer': ({ showCrossSell }) => showCrossSell,
});

export function ProductPriceRow({
  productId,
  brand,
  name,
  description,
  quantity,
  pricePerItem,
  showDetails,
  currencyIsoCode,
  hasPaddingBottom,
  onShowProductDetailsClick,
  hasBorder,
  isMpsProduct,
  cartProducts,
  lightContentAvailable,
  cartProductType,
  productLinksTitles,
  shipments,
  isFittingAvailable,
  type,
  productUrl,
  choiceAwardBadges,
}) {
  let isValidForLink = isValidForProductLink(productUrl);
  const isLogoToHide = ['bridgestone', 'firestone'].indexOf(brand.toLowerCase()) !== -1;

  let brandElem = isValidForLink ? (
    <a
      className={`${isLogoToHide ? 'logos-to-hide-text' : ''}`}
      key={`row-two-cells-left-${productId}`}
      href={productUrl}
    >
      {isLogoToHide ? brand.toLowerCase() : brand}
    </a>
  ) : (
    <span className={`${isLogoToHide ? 'logos-to-hide-text' : ''}`} key={`row-two-cells-left-${productId}`}>
      {isLogoToHide ? brand.toLowerCase() : brand}
    </span>
  );

  if (isCrossSellProduct(cartProductType, lightContentAvailable)) {
    brandElem = (
      <span
        key={`row-two-cells-left-${productId}`}
        className={'underline cursor-pointer'}
        onClick={(e) => onCrossSellDetailsClick({ spinachId: productId })}
      >
        {brand}
      </span>
    );
  }

  if (!showDetails) {
    return (
      <RowTwoCells
        key={`row-two-cells-${productId}`}
        name={lazyClassNames({ showDetails })}
        left={brandElem}
        right={getTranslationElement(
          'Details',
          {},
          {
            onClick: onShowProductDetailsClick,
            className: 'product-price__show-details',
          },
        )}
      />
    );
  }

  let labelExtraChildren = [getLabelProductName(name), getLabelProductDescription(description, productLinksTitles)];

  if (!isCrossSellProduct(cartProductType, lightContentAvailable) && isValidForLink) {
    labelExtraChildren = [<a href={productUrl}>{labelExtraChildren}</a>];
  }
  labelExtraChildren = [<div style={{ display: 'grid' }}>{labelExtraChildren}</div>];

  if (cartProducts.length > 0) {
    for (let key in cartProducts) {
      let cartProduct = cartProducts[key];
      if (
        cartProduct !== 'undefined' &&
        cartProduct.hasOwnProperty('productStock') &&
        cartProduct.id === productId &&
        true === cartProduct.productStock.isMpsProduct
      ) {
        isMpsProduct = true;
        break;
      }
    }
  }

  const content = isMpsProduct ? (
    <PriceIsMps
      key={`product-single-item-price-${productId}`}
      name={`product-single-item-price ${
        isCrossSellProduct(cartProductType, lightContentAvailable) ? 'underline cursor-pointer' : ''
      }`}
      label={brandElem}
      price={quantity * pricePerItem}
      currencyIsoCode={currencyIsoCode}
      hasBorder={hasBorder}
      hasPaddingBottom={hasPaddingBottom}
      labelExtraChildren={labelExtraChildren}
      onComponentClick={(e) =>
        isCrossSellProduct(cartProductType, lightContentAvailable) && onCrossSellDetailsClick({ spinachId: productId })
      }
    />
  ) : (
    <Price
      key={`product-single-item-price-${productId}`}
      name={lazyClassNames({ showDetails, showCrossSell: isCrossSellProduct(cartProductType, lightContentAvailable) })}
      label={brandElem}
      price={quantity * pricePerItem}
      currencyIsoCode={currencyIsoCode}
      hasBorder={hasBorder}
      hasPaddingBottom={hasPaddingBottom}
      labelExtraChildren={labelExtraChildren}
      middle={choiceAwardBadges}
      onComponentClick={(e) =>
        isCrossSellProduct(cartProductType, lightContentAvailable) && onCrossSellDetailsClick({ spinachId: productId })
      }
    />
  );

  const mainContent = (
    <>
      {content}
      {getProductExtraInfo({ showDetails, shipments, productId, currencyIsoCode, type, isFittingAvailable })}
    </>
  );

  return mainContent;
}

ProductPriceRow.propTypes = {
  productId: PropTypes.string,
  cartProductType: PropTypes.string,
  brand: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  quantity: PropTypes.number.isRequired,
  pricePerItem: PropTypes.number.isRequired,
  currencyIsoCode: PropTypes.string.isRequired,
  onShowProductDetailsClick: PropTypes.func,
  hasBorder: PropTypes.bool,
  hasPaddingBottom: PropTypes.bool,
  showDetails: PropTypes.bool,
  isMpsProduct: PropTypes.bool,
  lightContentAvailable: PropTypes.bool,
  cartProducts: PropTypes.arrayOf(PropTypes.object),
  shipments: PropTypes.arrayOf(PropTypes.object),
};

ProductPriceRow.defaultProps = {
  showDetails: true,
  hasBorder: true,
  hasPaddingBottom: true,
  isMpsProduct: false,
  cartProducts: [],
  shipments: [],
};

const isCrossSellProduct = (cartProductType, lightContentAvailable) => !!lightContentAvailable;

const getLabelProductName = (name) => (
  <div className="product-price__name" key="product-name" title={name}>
    {name}
  </div>
);

const getLabelProductDescription = (description, productLinksTitles) => (
  <>
    <div className="product-price__description" key="product-description" title={description}>
      {description}
    </div>
    {getLabelProductLinksTitles(productLinksTitles)}
  </>
);

const getLabelProductLinksTitles = (productLinksTitles) => {
  return (
    productLinksTitles &&
    productLinksTitles.map((tile) => (
      <div className="product-price__description" key={`product-links-titles-${tile}`} title={tile}>
        {tile}
      </div>
    ))
  );
};

const getLabelProductShipping = (shipments, productId, currencyIsoCode) => {
  return (
    shipments &&
    shipments.map(
      (shipment, index) =>
        shipment.items.filter((item) => item.productCode === productId).length > 0 && (
          <ShippingPrice
            hasAccessories={shipment.hasAccessories}
            hasWheels={shipment.hasWheels}
            hasTools={shipment.hasTools}
            shippingPrice={shipment.amount}
            shippingTime={`${shipment.shippingTime.daysFrom}-${shipment.shippingTime.daysTo}`}
            isMobileFitter={shipment.isMobileFitter}
            fromAddress={`${shipment.fromAddress.state}`}
            sidebarPostcode={`${shipment.toAddress.state}, ${shipment.toAddress.postcode}`}
            currencyIsoCode={currencyIsoCode}
            loading={shipment.loading || false}
            allowChangeShipping={false}
            onCalculateClick={() => {}}
            fromDetails={true}
            key={`ShippingPrice-details-${index}`}
          />
        ),
    )
  );
};

const getProductExtraInfo = ({ showDetails, shipments, productId, currencyIsoCode, type, isFittingAvailable }) => (
  <>
    {showDetails && getLabelProductShipping(shipments, productId, currencyIsoCode)}
    {type === 'gift_card' && (
      <tr>
        <td style={{ paddingBottom: '15px' }}>{getTranslationElement('cart-sent-via-email')}</td>
      </tr>
    )}
    {type === 'accessory' && isFittingAvailable && (
      <tr>
        <td style={{ paddingBottom: '15px' }} className="font-weight-light">
          {getTranslationElement('cart-accessory-text')}
        </td>
      </tr>
    )}
  </>
);

const isValidForProductLink = (url) => url && location.href.indexOf('/checkout/') === -1;
